import React from 'react';
import { Link } from 'react-router-dom';
import { FaUndo, FaPhone, FaEnvelope, FaListUl } from 'react-icons/fa';
import { CONTACT, phoneHref } from '../constants/contact';
import './PolicyPage.css';

const sections = [
  {
    id: 'overview',
    title: 'Refund Policy Overview',
    content: (
      <>
        <p>
          <strong>Nodex Global</strong>, operating <strong>MaxAutoParts</strong> at{' '}
          <strong>maxautoparts.online</strong>, wants you to be fully satisfied with every purchase. This Refund Policy
          explains how returns, refunds, and store credits are handled for orders placed through our website.
        </p>
        <p>
          All refunds are issued to the original payment method unless otherwise approved by our billing team. Processing
          times depend on your card issuer or bank.
        </p>
      </>
    ),
  },
  {
    id: 'eligible-returns',
    title: 'Eligible Returns',
    content: (
      <>
        <ul>
          <li>Unused, uninstalled parts in original condition may be returned within <strong>30 days</strong> of delivery</li>
          <li>Items must include original packaging, labels, and documentation</li>
          <li>A Return Merchandise Authorization (RMA) number is required before shipping any item back</li>
          <li>Proof of purchase (order number or invoice) must accompany all return requests</li>
        </ul>
      </>
    ),
  },
  {
    id: 'non-returnable',
    title: 'Non-Returnable Items',
    content: (
      <>
        <ul>
          <li>Electrical components once installed or opened (alternators, starters, sensors)</li>
          <li>Parts modified, damaged, or showing signs of installation</li>
          <li>Special-order, custom-sourced, or clearance items marked final sale</li>
          <li>Items returned after 30 days (warranty claims may still apply — see Warranty Policy)</li>
          <li>Fluids, gaskets, and consumables that have been opened</li>
        </ul>
      </>
    ),
  },
  {
    id: 'rma-process',
    title: 'RMA & Return Process',
    content: (
      <>
        <ol>
          <li>Email <a href="mailto:returns@maxautoparts.online">returns@maxautoparts.online</a> or call <a href={phoneHref}>{CONTACT.phone}</a> with your order number and reason for return</li>
          <li>Receive your RMA number and return shipping instructions within 1–2 business days</li>
          <li>Ship the item securely using a trackable carrier; retain your tracking number</li>
          <li>Our warehouse inspects the return within 5 business days of receipt</li>
        </ol>
        <p>Return shipping is the customer&apos;s responsibility unless the return is due to our error, wrong part shipped, or a verified defect.</p>
      </>
    ),
  },
  {
    id: 'refund-timeline',
    title: 'Refund Timeline',
    content: (
      <>
        <p>Once your return is approved:</p>
        <ul>
          <li>You will receive email confirmation of approval or rejection</li>
          <li>Refunds are processed to your original payment method within <strong>7–10 business days</strong></li>
          <li>Store credit may be offered at your request for faster resolution on future orders</li>
          <li>Original shipping charges are non-refundable unless the return is due to our error</li>
        </ul>
      </>
    ),
  },
  {
    id: 'cancellations',
    title: 'Order Cancellations',
    content: (
      <>
        <p>Orders may be cancelled for a full refund if they have not yet shipped. Contact us immediately at {CONTACT.phone}. Once an order has shipped, standard return procedures apply.</p>
        <p>Cancelled orders paid by check or wire may require additional processing time for refund issuance.</p>
      </>
    ),
  },
  {
    id: 'chargebacks',
    title: 'Chargebacks & Disputes',
    content: (
      <>
        <p>
          Please contact Nodex Global at <a href="mailto:billing@maxautoparts.online">billing@maxautoparts.online</a> before
          initiating a chargeback with your bank. We are committed to resolving billing disputes fairly and promptly.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact Nodex Global',
    content: (
      <>
        <ul>
          <li><strong>Company:</strong> {CONTACT.company}</li>
          <li><strong>Address:</strong> {CONTACT.address}</li>
          <li><strong>Website:</strong> {CONTACT.website}</li>
          <li><strong>Email:</strong> <a href="mailto:returns@maxautoparts.online">returns@maxautoparts.online</a></li>
          <li><strong>Phone:</strong> {CONTACT.phone}</li>
          <li><strong>Hours:</strong> Monday–Friday 8AM–8PM EST, Saturday 9AM–5PM EST</li>
        </ul>
      </>
    ),
  },
];

const RefundPolicy = () => (
  <div className="policy-page">
    <div className="policy-hero">
      <div className="policy-hero-badge">
        <FaUndo /> Refunds
      </div>
      <h1>Refund Policy</h1>
      <div className="policy-hero-meta">
        <span>Nodex Global</span>
        <span>|</span>
        <span>maxautoparts.online</span>
        <span>|</span>
        <span>Last Updated: May 16, 2026</span>
      </div>
    </div>

    <div className="policy-body">
      <div className="policy-toc">
        <h3><FaListUl /> Table of Contents</h3>
        <ol>
          {sections.map((s, i) => (
            <li key={i}>
              <a href={`#${s.id}`}>{s.title}</a>
            </li>
          ))}
        </ol>
      </div>

      {sections.map((s, i) => (
        <div className="policy-section" id={s.id} key={i}>
          <h2>
            <span className="section-num">{i + 1}</span>
            {s.title}
          </h2>
          {s.content}
        </div>
      ))}

      <div className="policy-contact-box">
        <h3>Questions About Refunds?</h3>
        <p>Our returns team at Nodex Global is ready to help with your RMA or refund request.</p>
        <div className="policy-contact-links">
          <a href="mailto:returns@maxautoparts.online" className="link-primary">
            <FaEnvelope /> returns@maxautoparts.online
          </a>
          <a href={phoneHref} className="link-secondary">
            <FaPhone /> {CONTACT.phone}
          </a>
        </div>
      </div>

      <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: '#a0aec0' }}>
        &copy; 2026 Nodex Global. All rights reserved. |{' '}
        <Link to="/privacy-policy" style={{ color: '#FFA500' }}>Privacy Policy</Link> |{' '}
        <Link to="/terms-of-service" style={{ color: '#FFA500' }}>Terms and Conditions</Link>
      </p>
    </div>
  </div>
);

export default RefundPolicy;
